import React, { useEffect, useState } from "react";
import {
  Loader2, BookOpen, Download, FileText, X, Copy, Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, API_BASE, formatApiError } from "@/lib/api";

const CATEGORY_COLOR = {
  Confidentiality: "bg-violet-50 text-violet-700 ring-violet-200",
  Services: "bg-blue-50 text-blue-700 ring-blue-200",
  Procurement: "bg-amber-50 text-amber-700 ring-amber-200",
  Partnership: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  Employment: "bg-rose-50 text-rose-700 ring-rose-200",
};

export default function Templates() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [open, setOpen] = useState(null); // full template object
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/templates");
        setItems(r.data.items || []);
      } catch (err) {
        toast.error("Couldn't load templates", { description: formatApiError(err) });
      } finally {
        setBusy(false);
      }
    })();
  }, []);

  const openTemplate = async (slug) => {
    try {
      const r = await api.get(`/templates/${slug}`);
      setOpen(r.data);
    } catch (err) {
      toast.error("Couldn't load template", { description: formatApiError(err) });
    }
  };

  const downloadTemplate = (slug) => {
    // Direct hit on the backend so the browser triggers a real save dialog
    window.open(`${API_BASE}/api/templates/${slug}/download`, "_blank");
  };

  const copyBody = async () => {
    if (!open) return;
    await navigator.clipboard.writeText(open.body);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="space-y-8" data-testid="templates-page">
      <div>
        <p className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-600">
          Library
        </p>
        <h1 className="mt-1.5 font-display text-3xl font-semibold tracking-tight text-slate-900">
          Safe Templates
        </h1>
        <p className="mt-1.5 text-sm text-slate-600 max-w-2xl">
          Pre-vetted, balanced starting points for the most common
          agreements. Each template is reviewer-friendly and ships with a
          legal-counsel reminder footer. Edit the bracketed fields and have
          your counsel review before signing.
        </p>
      </div>

      {busy ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
             data-testid="templates-grid">
          {items.map((t) => (
            <article
              key={t.slug}
              className="group relative rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow"
              data-testid={`template-${t.slug}`}
            >
              <div className="flex items-start justify-between gap-3">
                <FileText className="h-5 w-5 text-blue-600 shrink-0" />
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold ring-1 ring-inset ${
                    CATEGORY_COLOR[t.category] || "bg-slate-50 text-slate-700 ring-slate-200"
                  }`}
                >
                  {t.category}
                </span>
              </div>
              <h2 className="mt-3 font-display text-base font-semibold text-slate-900">
                {t.title}
              </h2>
              <p className="mt-2 text-sm text-slate-600 line-clamp-3">
                {t.summary}
              </p>
              <div className="mt-4 flex items-center gap-2">
                <Button
                  variant="outline"
                  className="h-8 flex-1"
                  onClick={() => openTemplate(t.slug)}
                  data-testid={`template-${t.slug}-view`}
                >
                  <BookOpen className="h-3.5 w-3.5 mr-1.5" /> View
                </Button>
                <Button
                  className="h-8 flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => downloadTemplate(t.slug)}
                  data-testid={`template-${t.slug}-download`}
                >
                  <Download className="h-3.5 w-3.5 mr-1.5" /> Download
                </Button>
              </div>
            </article>
          ))}
        </div>
      )}

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="template-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span>{open?.title}</span>
              {open?.category && (
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold ring-1 ring-inset ${
                  CATEGORY_COLOR[open.category] || "bg-slate-50 text-slate-700 ring-slate-200"
                }`}>
                  {open.category}
                </span>
              )}
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600 mt-1">
              {open?.summary}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto -mx-6 px-6 py-2 my-2 border-y border-slate-100 bg-slate-50/40">
            <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed text-slate-800">
{open?.body}
            </pre>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="outline" className="h-9" onClick={copyBody} data-testid="template-copy">
              {copied ? (
                <><Check className="h-3.5 w-3.5 mr-1.5 text-emerald-600" /> Copied</>
              ) : (
                <><Copy className="h-3.5 w-3.5 mr-1.5" /> Copy text</>
              )}
            </Button>
            <Button
              className="h-9 bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => downloadTemplate(open.slug)}
              data-testid="template-modal-download"
            >
              <Download className="h-3.5 w-3.5 mr-1.5" /> Download .md
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
